<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex min-w-0 items-start gap-3">
            <HistoryBackButton />
            <div class="min-w-0">
              <p class="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Hashtag</p>
              <h1 class="mt-1 truncate text-2xl font-semibold text-slate-950 sm:text-3xl">#{{ hashtag }}</h1>
              <p class="mt-1 text-sm text-slate-500">Flyers tagged with #{{ hashtag }}.</p>
            </div>
          </div>

          <span class="inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700">
            <i class="ri-hashtag" aria-hidden="true"></i>
            <span>{{ flyers.length }} flyer{{ flyers.length === 1 ? '' : 's' }}</span>
          </span>
        </div>
      </div>

      <div class="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <p class="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Results</p>
            <h2 class="mt-1 text-xl font-semibold text-slate-950">Tagged flyers</h2>
          </div>
          <button
            class="inline-flex min-h-9 items-center gap-2 rounded-full bg-slate-100 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
            type="button"
            @click="loadHashtagFlyers"
          >
            <i class="ri-refresh-line" aria-hidden="true"></i>
            <span>Refresh</span>
          </button>
        </div>

        <div v-if="isLoading" class="hashtag-flyers-grid mt-5">
          <div
            v-for="(column, columnIndex) in skeletonFlyerColumns"
            :key="`hashtag-skeleton-column-${columnIndex}`"
            class="hashtag-flyers-column"
          >
            <FlyerCardSkeleton v-for="index in column" :key="`hashtag-skeleton-${index}`" :index="index" />
          </div>
        </div>

        <div v-else-if="loadError || !flyers.length" class="py-12 text-center">
          <p class="m-0 text-lg font-semibold text-slate-900">{{ loadError ? 'Unable to load flyers' : 'No flyers found' }}</p>
          <p class="mt-2 text-sm font-normal text-slate-500">
            {{ loadError || `No flyers are currently tagged with #${hashtag}.` }}
          </p>
        </div>

        <div v-else class="hashtag-flyers-grid mt-5">
          <div
            v-for="(column, columnIndex) in flyerColumns"
            :key="`hashtag-column-${columnIndex}`"
            class="hashtag-flyers-column"
          >
            <FlyerCard v-for="flyer in column" :key="flyer.id" :flyer="flyer" view="masonry" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import FlyerCard from '@/components/common/FlyerCard.vue'
import FlyerCardSkeleton from '@/components/common/FlyerCardSkeleton.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { firstValue, normalizeFlyerDetail } from '@/utils/flyerDetail'

const route = useRoute()
const session = useSessionStore()
const isLoading = ref(false)
const loadError = ref('')
const flyers = ref([])
const masonryColumnCount = ref(1)

const hashtag = computed(() => String(route.params.hashtag || '').replace(/^#/, '').trim())

const distributeByColumn = (items, columnCount) => {
  const count = Math.max(1, Number(columnCount) || 1)
  const columns = Array.from({ length: count }, () => [])
  items.forEach((item, index) => {
    columns[index % count].push(item)
  })
  return columns
}

const flyerColumns = computed(() => distributeByColumn(flyers.value, masonryColumnCount.value))
const skeletonFlyerColumns = computed(() => distributeByColumn(Array.from({ length: 8 }, (_, index) => index + 1), masonryColumnCount.value))

const updateMasonryColumnCount = () => {
  if (typeof window === 'undefined') return
  masonryColumnCount.value = window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 2 : 1
}

const flattenGroupedRows = (groups = {}) =>
  Object.entries(groups || {}).flatMap(([dateKey, items = []]) =>
    Array.isArray(items)
      ? items.map((item) => ({
          ...item,
          time_ago: item.time_ago || item.time_Ago || item.timeAgo || '',
          time_Ago: item.time_Ago || item.time_ago || item.timeAgo || '',
          created_date: item.created_date || item.date || dateKey,
          groupDate: dateKey
        }))
      : []
  )

const extractRows = (payload) => {
  const candidates = [
    payload?.list,
    payload?.data,
    payload?.flyers,
    payload?.result,
    payload?.hashtag_flyers,
    payload?.data?.list,
    payload?.data?.data,
    payload?.data?.flyers,
    payload?.data?.result
  ]

  const rows = candidates.find((candidate) => candidate && (Array.isArray(candidate) || typeof candidate === 'object'))
  if (Array.isArray(rows)) return rows
  if (rows && typeof rows === 'object') return flattenGroupedRows(rows)
  return []
}

const normalizeHashtagFlyer = (item = {}, index = 0) => {
  const normalized = normalizeFlyerDetail(item, { id: `hashtag-${index}` })
  return {
    ...normalized,
    id: firstValue(normalized.id, normalized.flyer_id, `hashtag-${index}`),
    saved: Boolean(normalized.saved),
    liked: Boolean(normalized.liked)
  }
}

const loadHashtagFlyers = async () => {
  const tag = hashtag.value
  if (!tag) {
    flyers.value = []
    loadError.value = 'No hashtag was selected.'
    return
  }

  isLoading.value = true
  loadError.value = ''

  try {
    const id = session.userId || session.user?.user_id || session.user?.userid || session.user?.id || ''
    const { data } = await api.hashtagFlyers({ id, hashtag: tag })
    flyers.value = extractRows(data).map(normalizeHashtagFlyer)
  } catch (error) {
    flyers.value = []
    loadError.value = error?.response?.data?.message || error?.message || 'Hashtag flyers could not be loaded.'
  } finally {
    isLoading.value = false
  }
}

watch(hashtag, loadHashtagFlyers)

onMounted(() => {
  updateMasonryColumnCount()
  window.addEventListener('resize', updateMasonryColumnCount)
  loadHashtagFlyers()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMasonryColumnCount)
})
</script>

<style scoped>
.hashtag-flyers-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.hashtag-flyers-column {
  display: grid;
  min-width: 0;
  gap: 1rem;
  align-content: start;
}

.hashtag-flyers-column :deep(.flyer-card-skeleton) {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .hashtag-flyers-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .hashtag-flyers-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
